// frontend/src/components/DocumentView.js - Updated with new architecture
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import EditableTitle from './EditableTitle';
import AlignedTextPanel from './AlignedTextPanel';
import CreditPurchaseModal from './CreditPurchaseModal';
import OverloadedModal from './OverloadedModal';
import BatchTranslation from './BatchTranslation';
import AlertMessage from './AlertMessage';
import useDocumentViewController from '../controllers/DocumentViewController';

// Import services
import documentService from '../services/document/documentService';
import projectService from '../services/project/projectService';
import creditService from '../services/credit/creditService';

import '../styles/global.css';
import './DocumentView.css';

/**
 * Enhanced DocumentView with clearer separation of concerns using
 * the DocumentViewController to manage state and operations
 */
const DocumentView = () => {
    const { documentId } = useParams();
    const navigate = useNavigate();
    const { creditBalance, updateCreditBalance } = useContext(AuthContext);
    
    // Document and project data
    const [documentData, setDocumentData] = useState(null);
    const [projectDetails, setProjectDetails] = useState(null);
    const [projectId, setProjectId] = useState(null);
    const [docText, setDocText] = useState('');
    const [isDocumentFullyTranslated, setIsDocumentFullyTranslated] = useState(false);
    
    // UI state
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showOverloadedModal, setShowOverloadedModal] = useState(false);
    const [insufficientCredits, setInsufficientCredits] = useState(false);
    const [glossaryTerms, setGlossaryTerms] = useState([]);
    
    // Initialize the controller
    const controller = useDocumentViewController();
    const {
        batches,
        currentBatch,
        expandedBatchId,
        loading,
        translating,
        error,
        successMessage,
        savingState,
        batchAlignments,
        processingState,
        activeWordData,
        highlightDirection,
        
        // Actions
        loadBatches,
        toggleBatchExpansion,
        handleWordHover,
        handleTextChange,
        regenerate,
        
        // Setters
        setError,
        setSuccessMessage,
        setLoading
    } = controller;
    
    // Scroll synchronization
    const [panelRefs, setPanelRefs] = useState({});
    const isScrollingSyncRef = React.useRef(false);
    
    // Load document data and batches
    useEffect(() => {
        const loadDocumentAndProjectData = async () => {
            try {
                // setLoading(true); // Use DocumentView's own setLoading state
                // setError('');   // Use DocumentView's own setError state
                
                // Fetch document details
                const docData = await documentService.getDocumentById(documentId);
                if (!docData || docData.error) {
                    // setError(docData?.message || 'Document not found'); // Use own setError
                    // setLoading(false); // Use own setLoading
                    // Use controller's error if it's for controller operations
                    controller.setError(docData?.message || 'Document not found');
                    return;
                }
                setDocumentData(docData);
                const currentProjectId = docData.project_id; // Get projectId
                setProjectId(currentProjectId); // Update local projectId state if needed for other parts of DocumentView

                // Fetch document text
                const text = await documentService.getDocumentText(docData.file_url);
                setDocText(text);

                // Call controller's loadBatches, passing the projectId.
                // The controller will handle fetching project languages internally.
                if (currentProjectId) {
                    await controller.loadBatches(documentId, currentProjectId);
                } else {
                    console.error("Project ID is missing, cannot load batches with language context.");
                    await controller.loadBatches(documentId, null); // Load batches without specific language context
                }

                // Fetch glossary terms (if not already handled by controller or if needed here)
                if (currentProjectId) {
                    try {
                        const terms = await projectService.getGlossary(currentProjectId);
                        setGlossaryTerms(terms || []);
                    } catch (error) {
                        console.error("Error loading glossary:", error);
                    }
                }

                const restrictionStatus = await creditService.checkRestrictionStatus();
                // setInsufficientCredits(restrictionStatus && creditBalance <= 0); // Use own state

                // setLoading(false); // Use own setLoading
            } catch (err) {
                console.error("Error loading document:", err);
                // setError('Failed to load document data'); // Use own setError
                // setLoading(false); // Use own setLoading
                controller.setError('Failed to load document data: ' + (err.message || 'Unknown error'));
            }
        };

        loadDocumentAndProjectData();
        // Ensure dependencies are correct. `controller.loadBatches` should be stable.
    }, [documentId, creditBalance, controller.loadBatches, controller.setError]);
    
    // Handle batch translation completion
    const handleTranslationComplete = async (batchId, result) => {
        // Refresh batches to get the translated text
        await loadBatches(documentId);
        await updateCreditBalance();
        
        const creditsUsed = result.credits_used || 0;
        setSuccessMessage(`Translation completed (${creditsUsed.toLocaleString()} credits used)`);
    };
    
    // Handle translation errors
    const handleTranslationError = (errorType, error) => {
        if (errorType === 'credits') {
            setInsufficientCredits(true);
            setShowPurchaseModal(true);
        } else if (errorType === 'overloaded') {
            setShowOverloadedModal(true);
        } else {
            setError(error?.message || 'Translation failed');
        }
    };
    
    // Download the complete translated document
    const download = async () => {
        try {
            setLoading(true);
            setError('');
            setSuccessMessage('');
            
            // Use translation service for document download
            const result = await documentService.downloadTranslatedDocument(documentId);
            
            if (result.error) {
                throw new Error(result.message || 'Download failed');
            }
            
            // Get file details from the document data
            const fileExtension = documentData?.file_type || 'txt';
            const baseName = documentData?.title || 'document';
            const fileName = `${baseName}-translated.${fileExtension}`;
            
            // Create a blob for download
            const blob = new Blob([result.content], { type: `text/${fileExtension}` });
            const url = window.URL.createObjectURL(blob);
            
            // Create a temporary anchor to trigger download
            const a = window.document.createElement('a');
            a.href = url;
            a.download = fileName;
            window.document.body.appendChild(a);
            a.click();
            
            // Clean up
            window.URL.revokeObjectURL(url);
            window.document.body.removeChild(a);
            
            setSuccessMessage(`Document downloaded as ${fileName}`);
        } catch (err) {
            console.error("Download error:", err);
            setError('Failed to download translated document. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    // Scroll handler for syncing panels
    const handleScroll = (batchId) => (scrollData) => {
        // Skip if we're already handling a scroll event
        if (isScrollingSyncRef.current) return;
        
        // Handle element registration
        if (scrollData.type === 'registerRef') {
            setPanelRefs(prev => ({
                ...prev,
                [`${batchId}-${scrollData.isSource ? 'source' : 'target'}`]: scrollData.element
            }));
            return;
        }
        
        // Handle scroll syncing
        try {
            // Set flag to prevent recursive scrolling
            isScrollingSyncRef.current = true;
            
            // Calculate scroll percentage
            const scrollPercentage = scrollData.scrollTop / 
                (scrollData.scrollHeight - scrollData.clientHeight || 1);
            
            // Get the paired panel
            const targetKey = `${batchId}-${scrollData.isSource ? 'target' : 'source'}`;
            const targetPanel = panelRefs[targetKey];
            
            if (targetPanel) {
                // Apply the same relative scroll position
                const targetScrollTop = scrollPercentage * 
                    (targetPanel.scrollHeight - targetPanel.clientHeight || 1);
                targetPanel.scrollTop = targetScrollTop;
            }
        } finally {
            // Small delay before allowing another scroll event
            setTimeout(() => {
                isScrollingSyncRef.current = false;
            }, 10);
        }
    };
    
    // Determine if there are any completed batches for download button state
    const hasCompletedBatches = batches.some(batch => 
        batch.translated_text && batch.translated_text.trim() !== ''
    );
    
    // Render helper for save status indicator
    const renderSaveStatus = (batchId) => {
        const status = savingState[batchId];
        if (!status) return null;
        
        if (status.status === 'saving') {
            return <span className="autosave-status autosave-saving">Autosaving...</span>;
        } else if (status.status === 'saved') {
            return <span className="autosave-status autosave-saved">Autosaved</span>;
        } else if (status.status === 'error') {
            return <span className="autosave-status autosave-error">{status.message || 'Error'}</span>;
        }
        
        return null;
    };

    return (
        <div className="container">
            <div className="back-button" onClick={() => navigate(projectId ? `/projects/${projectId}` : '/')}>
                ← Back to Project
            </div>

            <div className="document-view-container">
                <div className="document-header">
                    <div className="document-title">
                        {documentData && (
                            <EditableTitle 
                                documentId={documentId}
                                initialTitle={documentData.title}
                                onUpdate={(updatedDoc) => {
                                    setDocumentData({...documentData, ...updatedDoc});
                                }}
                                fontSize="text-2xl"
                                className="mr-2"
                            />
                        )}
                        <span className="document-type-badge">{documentData?.file_type.toUpperCase()}</span>
                    </div>
                </div>

                {error && (
                  <AlertMessage 
                    message={error} 
                    type="error" 
                    onDismiss={() => setError('')}
                  >
                    {insufficientCredits && (
                      <button 
                        onClick={() => setShowPurchaseModal(true)}
                        className="btn btn-success btn-sm ml-4"
                      >
                        Purchase Credits
                      </button>
                    )}
                  </AlertMessage>
                )}
                
                {successMessage && (
                  <AlertMessage 
                    message={successMessage} 
                    type="success" 
                    onDismiss={() => setSuccessMessage('')} 
                  />
                )}

                {/* Only show when credits have gone from positive to zero */}
                {insufficientCredits && creditBalance <= 0 && (
                    <div className="zero-credits-message mb-4">
                        <span>You've run out of translation credits.</span>
                        <button 
                            onClick={() => setShowPurchaseModal(true)}
                            className="btn btn-success btn-sm"
                        >
                            Purchase Credits
                        </button>
                    </div>
                )}

                <div className="original-document">
                    <h2 className="mb-3">Original Document</h2>
                    <div className="original-document-content">
                        {docText || 'Loading document content...'}
                    </div>
                </div>

                <div className="batches-section">
                  <div className="batches-header">
                    <h2>Translation Batches</h2>
                  </div>
                  
                  <div className="space-y-4">
                    {batches
                      .sort((a, b) => a.batch_number - b.batch_number)
                      .map(batch => (
                        <div key={batch.id} className="batch-item">
                          <div 
                            className={`batch-header ${expandedBatchId === batch.id ? 'expanded' : ''}`}
                            onClick={() => toggleBatchExpansion(batch.id, glossaryTerms)}
                          >
                            <div className="batch-number">
                              Batch {batch.batch_number}
                              <span className={`batch-status ${batch.status}`}>
                                {batch.status === 'completed' ? '✓ Machine-Translated' : 
                                 batch.status === 'edited' ? '✏️ Edited' :
                                 batch.status === 'error' ? '✗ Error' : 
                                 translating && currentBatch?.id === batch.id ? '⋯ Pending' : 
                                 (!batch.translated_text || batch.translated_text.trim() === '') ? '✗ Error' : '✓ Completed'}
                              </span>
                              {/* Add the autosave status indicator */}
                              {renderSaveStatus(batch.id)}
                            </div>
                            <span>{expandedBatchId === batch.id ? 'Hide' : 'Show'}</span>
                          </div>
                          
                          {expandedBatchId === batch.id && (
                            <div className="batch-content">
                              <div className="batch-panels-container">
                                {/* Source panel */}
                                <div className="panel">
                                  <div className="panel-header">
                                    <h3 className="panel-title">Original Text</h3>
                                  </div>
                                  <div className="panel-content">
                                    <AlignedTextPanel 
                                      text={batch.original_text || ''}
                                      alignments={batchAlignments[batch.id] || []}
                                      isSource={true}
                                      isEditable={false}
                                      onScroll={handleScroll(batch.id)}
                                      onWordHover={handleWordHover}
                                      activeWordData={activeWordData}
                                      highlightDirection={highlightDirection}
                                      className="panel-content-readonly sync-scroll"
                                    />
                                  </div>
                                </div>
                                
                                {/* Target panel */}
                                <div className="panel">
                                  <div className="panel-header">
                                    <h3 className="panel-title">Translated Text</h3>
                                  </div>
                                  <div className="panel-content">
                                    <AlignedTextPanel 
                                      text={batch.translated_text || ''}
                                      alignments={batchAlignments[batch.id] || []}
                                      isSource={false}
                                      isEditable={true}
                                      onTextChange={(text) => handleTextChange(batch.id, text)}
                                      onScroll={handleScroll(batch.id)}
                                      onWordHover={handleWordHover}
                                      activeWordData={activeWordData}
                                      highlightDirection={highlightDirection}
                                      className="panel-content-editable sync-scroll"
                                    />
                                  </div>
                                  
                                  <div className="button-container">
                                    <button 
                                      onClick={() => regenerate(batch.id, glossaryTerms)}
                                      disabled={translating || insufficientCredits || creditBalance <= 0}
                                      className="button button-secondary"
                                    >
                                      {translating && currentBatch?.id === batch.id ? 'Processing...' : 'Regenerate'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    
                    {batches.length === 0 && !loading && (
                      <div className="text-center p-6 bg-gray-50 rounded border border-gray-200">
                        <p className="text-gray-500 mb-4">No batches have been translated yet. Click "Translate Next Batch" to begin.</p>
                      </div>
                    )}
                    
                    {loading && batches.length === 0 && (
                      <div className="text-center p-6 bg-gray-50 rounded border border-gray-200">
                        <div className="loading">Loading translation batches...</div>
                      </div>
                    )}
                  </div>
                  
                  <BatchTranslation
                    documentId={documentId}
                    projectId={projectId}
                    onBatchCreated={(newBatch, isComplete) => {
                      if (isComplete) setIsDocumentFullyTranslated(true);
                    }}
                    onTranslationComplete={handleTranslationComplete}
                    onError={handleTranslationError}
                    disabled={isDocumentFullyTranslated || translating}
                  />
                </div>
               
                {/* Always show download section but with conditional message */}
                <div className="download-section">
                    {isDocumentFullyTranslated && hasCompletedBatches ? (
                        <p className="mb-4">All batches have been translated! You can now download the complete document.</p>
                    ) : hasCompletedBatches ? (
                        <p className="mb-4">Download the currently translated content as a document.</p>
                    ) : batches.length > 0 ? (
                        <p className="mb-4">Complete at least one batch translation to enable downloading.</p>
                    ) : (
                        <p className="mb-4">Translate some content to enable downloading.</p>
                    )}
                    
                    <button 
                        onClick={download}
                        disabled={loading || batches.length === 0 || !hasCompletedBatches}
                        className="button button-success"
                    >
                        {loading ? 'Processing...' : 'Download Translated Document'}
                    </button>
                </div>
            </div>

            {/* Credit Purchase Modal */}
            <CreditPurchaseModal 
                isOpen={showPurchaseModal}
                onClose={() => {
                    setShowPurchaseModal(false);
                    
                    // Reset the insufficientCredits flag
                    setInsufficientCredits(false);
                    
                    // Refresh credit balance
                    updateCreditBalance();
                    
                    // Clear error messages
                    setError('');
                    
                    // Show confirmation
                    setSuccessMessage('Credits added successfully. You can now continue translating.');
                }}
            />

            {/* Overloaded Service Modal */}
            <OverloadedModal 
                isOpen={showOverloadedModal}
                onClose={() => setShowOverloadedModal(false)}
            />
        </div>
    );
};

export default DocumentView;